/**
 * File Upload Component
 * Drag and drop file upload with preview
 * 
 * @author Varun Pandey
 */

import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, FileText, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

export interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
  progress?: number;
}

interface FileUploadProps {
  label?: string;
  description?: string;
  accept?: Record<string, string[]>;
  maxFiles?: number;
  maxSize?: number;
  files: UploadedFile[];
  onChange: (files: UploadedFile[]) => void;
  error?: string;
  className?: string;
}

const defaultAccept = {
  'image/*': ['.png', '.jpg', '.jpeg', '.webp'],
  'application/pdf': ['.pdf'],
};

export const FileUpload: React.FC<FileUploadProps> = ({
  label,
  description = 'Drag and drop files here, or click to browse',
  accept = defaultAccept,
  maxFiles = 5,
  maxSize = 5 * 1024 * 1024, // 5MB
  files,
  onChange,
  error,
  className,
}) => {
  const [dragError, setDragError] = useState<string | null>(null);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    setDragError(null);

    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(r => {
        if (r.errors?.[0]?.code === 'file-too-large') {
          return `${r.file.name} is too large (max ${maxSize / 1024 / 1024}MB)`;
        }
        if (r.errors?.[0]?.code === 'file-invalid-type') {
          return `${r.file.name} is not a supported file type`;
        }
        return `${r.file.name} could not be uploaded`;
      });
      setDragError(errors.join(', '));
      return;
    }

    const totalFiles = files.length + acceptedFiles.length;
    if (totalFiles > maxFiles) {
      setDragError(`You can only upload up to ${maxFiles} files`);
      return;
    }

    const newFiles: UploadedFile[] = acceptedFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
    }));

    onChange([...files, ...newFiles]);
  }, [files, onChange, maxFiles, maxSize]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept,
    maxFiles: maxFiles - files.length,
    maxSize,
    disabled: files.length >= maxFiles,
  });

  const removeFile = (fileId: string) => {
    const updatedFiles = files.filter(f => f.id !== fileId);
    // Revoke object URL to prevent memory leaks
    const removedFile = files.find(f => f.id === fileId);
    if (removedFile?.preview) {
      URL.revokeObjectURL(removedFile.preview);
    }
    onChange(updatedFiles);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isDisabled = files.length >= maxFiles;

  return (
    <div className={cn('w-full', className)}>
      {label && (
        <label className="mb-2 block text-sm font-medium text-white">
          {label}
        </label>
      )}

      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={cn(
          'relative rounded-[0.75rem]  transition-colors cursor-pointer',
          isDragActive && !isDragReject && 'border-[#F33363] bg-[#F33363]/10',
          isDragReject && 'border-destructive bg-destructive/10',
          !isDragActive && !isDragReject && 'border-[#3A3A3C] bg-[#2C2C2E] hover:border-[#4A4A4C]',
          isDisabled && 'opacity-50 cursor-not-allowed',
          (error || dragError) && 'border-destructive'
        )}
      >
        <input {...getInputProps()} />
        <div className="flex flex-col items-center justify-center py-8 px-4">
          <div className={cn(
            'flex h-12 w-12 items-center justify-center rounded-full mb-3',
            isDragActive ? 'bg-[#F33363]/20' : 'bg-[#3A3A3C]'
          )}>
            <Upload className={cn(
              'h-6 w-6',
              isDragActive ? 'text-[#F33363]' : 'text-[#8E8E93]'
            )} />
          </div>
          <p className="text-sm text-white text-center mb-1">
            {isDragActive ? 'Drop files here...' : description}
          </p>
          <p className="text-xs text-[#8E8E93] text-center">
            PNG, JPG, PDF up to {maxSize / 1024 / 1024}MB
          </p>
          {files.length > 0 && (
            <p className="text-xs text-[#8E8E93] mt-2">
              {files.length} of {maxFiles} files uploaded
            </p>
          )}
        </div>
      </div>

      {/* Error Messages */}
      {(error || dragError) && (
        <div className="mt-2 flex items-start gap-2 text-destructive">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <p className="text-xs">{error || dragError}</p>
        </div>
      )}

      {/* File Previews */}
      {files.length > 0 && (
        <div className="mt-4 space-y-2">
          {files.map((uploadedFile) => (
            <div
              key={uploadedFile.id}
              className="flex items-center gap-3 p-3 rounded-lg bg-[#2C2C2E]"
            >
              {/* Preview/Icon */}
              {uploadedFile.preview ? (
                <img
                  src={uploadedFile.preview}
                  alt={uploadedFile.file.name}
                  className="h-10 w-10 rounded object-cover flex-shrink-0"
                />
              ) : (
                <div className="flex h-10 w-10 items-center justify-center rounded bg-[#3A3A3C] flex-shrink-0">
                  {uploadedFile.file.type === 'application/pdf' ? (
                    <FileText className="h-5 w-5 text-[#F33363]" />
                  ) : (
                    <ImageIcon className="h-5 w-5 text-[#8E8E93]" />
                  )}
                </div>
              )}

              {/* File Info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{uploadedFile.file.name}</p>
                <p className="text-xs text-[#8E8E93]">
                  {formatFileSize(uploadedFile.file.size)}
                </p>
              </div>

              {/* Remove Button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(uploadedFile.id);
                }}
                className="flex h-8 w-8 items-center justify-center rounded-full text-[#8E8E93] hover:bg-[#3A3A3C] hover:text-white transition-colors flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
